<script setup>
import { LControlLayers, LGeoJson, LMap, LTileLayer } from "@vue-leaflet/vue-leaflet"
import "leaflet/dist/leaflet.css"
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import LFullScreen from "vue-leaflet-fullscreen"
import "vue-leaflet-fullscreen/dist/style.css"
import axios from '@/helpers/axios'
import FilesList from '@/views/maps/FilesList.vue'
import moment from "moment/moment";

definePage({
  meta: {
    layout: 'default',
    role: 'user'
  },
})

const { t } = useI18n()
const route = useRoute()
const uuid = route.params.uuid
const map = ref(null)
const zoom = ref(17)
const center = ref([0, 0])
const bounds = ref(null)
const accessToken = ref('pk.eyJ1IjoicmVuYXRvbXMiLCJhIjoiY2pzcDNpaDdlMHFvcTQ5cWx3eXJlMDZ2eiJ9.3GsYRV5ahGFqCpb01u7qFw')
const urlOutdoors = ref('https://api.mapbox.com/styles/v1/mapbox/outdoors-v11/tiles/{z}/{x}/{y}?access_token=' + accessToken.value)
const urlSattelite = ref('https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/{z}/{x}/{y}?access_token=' + accessToken.value)

const mapRef = ref()

const geojsonStyle = ref({
  //weight: 2,
  //color: "#ECEFF1",
  //opacity: 1,
  //fillColor: "#3388FF",
  fillOpacity: 0
})

function onMapReady(map) {
  mapRef.value = map
}

const downloadReport = () => {
  axios.get('/maps/' + uuid + '/report').then(response => {
    window.location.href = response.data.url;
  }).catch(e => {
    console.error(e)
  })
}

const updateData = () => {
  axios.get('/maps/' + uuid).then(response => {
    map.value = response.data
    center.value = response.data.bounds.center.reverse()
    bounds.value = [response.data.bounds.lowerLeft.reverse(), response.data.bounds.upperRight.reverse()]
  }).catch(e => {
    console.error(e)
  })
}

updateData()
</script>

<template>
  <div v-if="map">
    <div class="page-header mb-4">
      <div class="d-flex flex-column flex-sm-row align-center justify-space-between gap-4">
        <div class="flex-grow-1">
          <h2 class="text-h4 font-weight-semibold mb-2 text-high-emphasis">
            {{ map.name }}
          </h2>
        </div>
      </div>
    </div>

    <!-- Details Section -->
    <VCard class="observation-meta mb-4 pa-3" elevation="2" style="border-radius: 16px;">

      <div class="meta-item">
        <VIcon icon="tabler-calendar" class="meta-icon text-primary" />
        <span class="meta-text">
          {{ moment.utc(map.sampled_at).local().format('D MMMM YYYY') }}
        </span>
      </div>
      <div class="meta-separator"></div>
      <div class="meta-item">
        <VIcon icon="tabler-eye" class="meta-icon text-primary" />
        <span class="meta-text">
            {{ map.public === true ? $t('maps.public') : $t('maps.private') }}
        </span>
      </div>
      <div v-if="map.area" class="meta-separator"></div>
      <div v-if="map.area" class="meta-item">
        <VIcon icon="tabler-marquee-2" class="meta-icon text-primary" />
        <span class="meta-text">
          <span v-html="map.area + ' m<sup>2</sup>'" />
        </span>
      </div>
      <div class="meta-separator"></div>
      <div class="meta-item">
        <VIcon icon="tabler-layers-selected" class="meta-icon text-primary" />
        <span class="meta-text">
            {{ $t('maps.layers_count', {number: map.layers.length}) }}
        </span>
      </div>

      <template v-for="data in map.extra_data" :key="data.field">
        <div class="meta-separator"></div>
        <div class="meta-item">
          <VIcon icon="tabler-layers-selected" class="meta-icon text-primary" />
          <span class="meta-text">
            <strong class="mr-1">{{ data.field + ':' }}</strong>{{ data.value }}
        </span>
        </div>
      </template>
    </VCard>

    <VCard
      class="overflow-visible pa-4 mb-4"
      style="border-radius: 16px;"
      elevation="2"
    >
      <div v-if="map.notes">
        <div v-if="map.notes" class="d-flex align-center mt-2 mt-sm-0">
          <VIcon icon="tabler-note" class="me-1" />
          <span class="text-subtitle-1">{{ map.notes }}</span>
        </div>
      </div>

      <div v-if="map.layers.length" class="d-flex align-center mt-2 mt-sm-0">
        <VIcon icon="tabler-layers-selected" class="me-1" />
        <div>
          <VChip
            v-for="layer in map.layers"
            :key="layer.uuid"
            variant="elevated"
            class="mr-2"
          >
            {{ layer.name }}
          </VChip>
        </div>
      </div>

      <div v-if="map.attachments && map.attachments.length" class="mt-2">
        <VRow>
          <VCol
            cols="12"
            order="2"
            order-sm="1"
          >
            <VRow>
              <VCol
                cols="12"
                class="text-no-wrap"
              >
                <span class="me-2">
                  <VIcon icon="tabler-paperclip" class="me-1" />
                  {{ $t('maps.attachments') }}
                </span>
                <FilesList
                  :files="map.attachments"
                  :uuid="uuid"
                  :allow-delete="false"
                  @update="updateData"
                />
                  
              </VCol>
            </VRow>
          </VCol>
        </VRow>
      </div>
    </VCard>

    <VCard
      class="pa-0 overflow-visible cursor-pointer"
      elevation="2"
      outlined
      style="border-radius: 16px;"
    >
      <VCardText class="pa-0">
        <div
          style="height: 500px; width: 100%; border-radius: 15px; overflow: hidden;"
        >
          <LMap
            v-model:zoom="zoom"
            :center="center"
            :max-bounds="bounds"
            :use-global-leaflet="false"
            @ready="onMapReady"
          >
            <LFullScreen :map-ref="mapRef" />
            <LTileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              layer-type="base"
              name="OpenStreetMap"
              :visible="false"
            />
            <LTileLayer
              :url="urlSattelite"
              layer-type="base"
              name="Mapbox Satellite"
              visible
            />
            <LTileLayer
              :url="urlOutdoors"
              layer-type="base"
              name="Mapbox Outdoors"
              :visible="false"
            />
            <LTileLayer
              v-for="(layer, index) in map.layers"
              :key="layer.uuid"
              :url="layer.url"
              layer-type="overlay"
              :options="{tms: 1, opacity: 1, attribution: '', minZoom: 2, maxZoom: 22}"
              :name="layer.name"
              :visible="index === 0"
            />
            <LGeoJson
              v-if="map.geojson"
              :geojson="map.geojson"
              :name="$t('maps.parcel')"
              layer-type="overlay"
              :visible="false"
              :options-style="geojsonStyle"
            />
            <LControlLayers
              :collapsed="false"
              position="topright"
            />
          </LMap>
        </div>
      </VCardText>
    </VCard>
  </div>
</template>


<style scoped>
.observation-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.meta-icon {
  opacity: 0.8;
}

.meta-text {
  font-size: 0.95rem;
  color: var(--v-theme-on-surface);
}

.meta-separator {
  width: 1px;
  height: 20px;
  background-color: rgba(0, 0, 0, 0.1);
}

</style>
